import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { FloatingReviewButton, ReviewsModal } from '@/components/ReviewsModal';
import { FrequentlyOrdered } from '@/components/Analytics';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Cake, Cookie, Gift, Soup } from 'lucide-react';
import { useState } from 'react';

const Home = () => {
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const categories = [
    {
      name: 'Fresh Breads',
      description: 'Artisan bread baked fresh daily',
      image: '/images/categories/breads.jpg',
      link: '/products?category=breads',
      icon: Soup
    },
    {
      name: 'Sourdough',
      description: 'Authentic sourdough bread',
      image: '/images/categories/classic-sourdough.jpg',
      link: '/products?category=sourdough',
      icon: Soup
    },
    {
      name: 'Healthy Cookies',
      description: 'Nutritious & delicious cookies',
      image: '/images/categories/cookies.jpg',
      link: '/products?category=cookies',
      icon: Cookie
    },
    {
      name: 'Custom Cakes',
      description: 'Birthday & anniversary cakes',
      image: '/images/categories/cakes.jpg',
      link: '/custom-cakes',
      icon: Cake
    },
    {
      name: 'Tea Cakes',
      description: 'Perfect desserts for tea time',
      image: '/images/categories/tea-cakes.jpg',
      link: '/products?category=tea-cakes',
      icon: Cake
    },
    {
      name: 'Gift Hampers',
      description: 'Curated bakery gift sets',
      image: '/images/categories/hampers.jpg',
      link: '/products?category=hampers',
      icon: Gift
    },
    {
      name: 'Sweet Bites',
      description: 'Bite-sized sweet desserts',
      image: '/images/categories/sweet-bites.jpg',
      link: '/products?category=sweet-bites',
      icon: Cookie
    },
    {
      name: 'Savoury Bites',
      description: 'Savory snacks & treats',
      image: '/images/categories/savoury-bites.jpg',
      link: '/products?category=savoury-bites',
      icon: Cookie
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <FloatingReviewButton onClick={() => setReviewsOpen(true)} />
      <ReviewsModal open={reviewsOpen} onOpenChange={setReviewsOpen} />
      
      <section className="relative bg-gradient-to-b from-amber-50 to-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-center gap-4 mb-4">
                <img 
                  src="/images/logo.png" 
                  alt="HHouse of Breadss - Best Bakery in Gurgaon" 
                  className="w-20 h-20 md:w-24 md:h-24 object-contain"
                  data-testid="brand-logo"
                />
                <h1 className="text-4xl md:text-5xl font-bold text-accent leading-tight" data-testid="hero-title">
                  HHOUSE OF BREADSS
                </h1>
              </div>
              <h2 className="text-xl md:text-2xl text-primary font-semibold">
                Gurgaon's Finest Artisan Bakery
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground">
                Discover fresh sourdough bread, healthy cookies, custom cake, birthday cake, anniversary cake, chocolate cake & delicious desserts. 100% eggless bakery with daily fresh baking delivered to your doorstep.
              </p>
              <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                <span className="bg-amber-100 px-3 py-1 rounded-full">Fresh Bread</span>
                <span className="bg-amber-100 px-3 py-1 rounded-full">Birthday Cake</span>
                <span className="bg-amber-100 px-3 py-1 rounded-full">Sourdough</span>
                <span className="bg-amber-100 px-3 py-1 rounded-full">Healthy Cookies</span>
                <span className="bg-amber-100 px-3 py-1 rounded-full">Chocolate Cake</span>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/products" data-testid="order-online-button">
                  <Button className="h-12 px-8 rounded-full bg-primary hover:bg-primary/90 font-medium transition-all hover:scale-105 active:scale-95 shadow-lg text-lg">
                    Order Online
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/custom-cakes">
                  <Button variant="outline" className="h-12 px-8 rounded-full font-medium transition-all hover:scale-105 border-primary text-primary">
                    Custom Cakes
                    <Cake className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <img
                src="/images/hero/hero-background.jpg"
                alt="Fresh bakery products - Bread, Cakes, Cookies and Desserts in Gurgaon"
                className="rounded-2xl shadow-2xl"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      <FrequentlyOrdered />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-semibold text-accent mb-4" data-testid="categories-title">
              Explore Our Collection
            </h2>
            <p className="text-lg text-muted-foreground">
              Fresh, eggless, and made with love
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
            {categories.map((category, index) => (
              <Link
                key={index}
                to={category.link}
                className="group relative overflow-hidden rounded-2xl aspect-[4/5] cursor-pointer"
                data-testid={`category-card-${category.name.toLowerCase().replace(' ', '-')}`}
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">{category.name}</h3>
                  <div className="flex items-center text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Shop Now</span>
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-b from-white to-amber-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-semibold text-accent mb-6">
              Why Choose Us?
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="space-y-3">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto">
                  <span className="text-2xl">🌱</span>
                </div>
                <h3 className="text-xl font-semibold text-accent">100% Eggless</h3>
                <p className="text-muted-foreground">All our products are completely eggless and made with high-quality ingredients</p>
              </div>
              <div className="space-y-3">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto">
                  <span className="text-2xl">🍞</span>
                </div>
                <h3 className="text-xl font-semibold text-accent">Fresh Daily</h3>
                <p className="text-muted-foreground">Baked fresh every day with love and care for the perfect taste</p>
              </div>
              <div className="space-y-3">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto">
                  <span className="text-2xl">🚚</span>
                </div>
                <h3 className="text-xl font-semibold text-accent">Fast Delivery</h3>
                <p className="text-muted-foreground">Quick and safe delivery to your doorstep in Gurugram</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
